import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Tabs } from "antd";
import { useLocation } from "react-router-dom";
import { journalsQueries } from "@/entities/journal";
import { useCoordinator, useNavigationPosition } from "@/shared/lib/hooks";
import { TitleJournalStatusBadge } from "@/shared/ui";
import { useNavigate, useAppRoutes } from "@shared/lib/react-router";
import { useJournalVersionsStore } from "../../journal-version-list";
import "./journal-tabs.scss";

const getTabs = ({
  projectId,
  objectId,
  mainJournalId,
  titleId,
  mainJournalVersionId,
  isJournalVersionMode,
  status,
  routes,
  // isFirstSelected,
}: {
  projectId: string;
  objectId: string;
  mainJournalId: string;
  titleId: string;
  mainJournalVersionId: string;
  isJournalVersionMode: boolean;
  status: string;
  routes: ReturnType<typeof useAppRoutes>;
  // isFirstSelected?: boolean;
}) => [
  {
    key: isJournalVersionMode
      ? routes.journals.journalVersionTitle(
          projectId,
          objectId,
          mainJournalId,
          titleId,
          mainJournalVersionId,
        )
      : routes.journals.title(projectId, objectId, mainJournalId, titleId),
    label: (
      <div className="journal-tabs-title-label">
        <div>Титульный лист</div>
        <div className="status-button-container">
          <TitleJournalStatusBadge status={status} />
        </div>
      </div>
    ),
  },
  {
    key: isJournalVersionMode
      ? routes.journals.journalVersionSection1(
          projectId,
          objectId,
          mainJournalId,
          titleId,
          mainJournalVersionId,
        )
      : routes.journals.section1(projectId, objectId, mainJournalId, titleId),
    label: "Раздел 1",
  },
  {
    key: isJournalVersionMode
      ? routes.journals.journalVersionSection2(
          projectId,
          objectId,
          mainJournalId,
          titleId,
          mainJournalVersionId,
        )
      : routes.journals.section2(projectId, objectId, mainJournalId, titleId),
    label: "Раздел 2",
  },
  {
    key: isJournalVersionMode
      ? routes.journals.journalVersionSection3(
          projectId,
          objectId,
          mainJournalId,
          titleId,
          mainJournalVersionId,
        )
      : routes.journals.section3(projectId, objectId, mainJournalId, titleId),
    label: "Раздел 3",
  },
  {
    key: isJournalVersionMode
      ? routes.journals.journalVersionSection4(
          projectId,
          objectId,
          mainJournalId,
          titleId,
          mainJournalVersionId,
        )
      : routes.journals.section4(projectId, objectId, mainJournalId, titleId),
    label: "Раздел 4",
  },
  {
    key: isJournalVersionMode
      ? routes.journals.journalVersionSection5(
          projectId,
          objectId,
          mainJournalId,
          titleId,
          mainJournalVersionId,
        )
      : routes.journals.section5(projectId, objectId, mainJournalId, titleId),
    label: "Раздел 5",
  },
  {
    key: isJournalVersionMode
      ? routes.journals.journalVersionSection6(
          projectId,
          objectId,
          mainJournalId,
          titleId,
          mainJournalVersionId,
        )
      : routes.journals.section6(projectId, objectId, mainJournalId, titleId),
    label: "Раздел 6",
  },
];

export const JournalTabs = () => {
  const navigate = useNavigate();
  const routes = useAppRoutes();
  const { projectId, titleId, objectId, mainJournalId, mainJournalVersionId } =
    useCoordinator();
  const { isJournalVersionTitle, isTitleVersionDetail, isTitleAmendmentsForm } =
    useNavigationPosition();
  const { data: journalTitle } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );
  const location = useLocation();

  const journalVersions = useJournalVersionsStore((s) => s.versions);
  const actualJournalVersionId =
    journalVersions.find((v) => v.status === "ACTUAL")?.versionId ||
    journalVersions[0]?.versionId ||
    "";

  const isJournalVersionMode =
    !!mainJournalVersionId &&
    (isJournalVersionTitle || location.pathname.includes("/versions/"));

  // Если мы на версии титульного листа (titleJournal/:titleId/version/:titleVersionId),
  // то разделы должны открываться в режиме версии ОЖР (актуальная версия),
  // иначе в хедере/свитчере теряется контекст.
  const isTabsVersionMode = isJournalVersionMode || isTitleVersionDetail;
  const tabsJournalVersionId = isJournalVersionMode
    ? mainJournalVersionId || ""
    : actualJournalVersionId;

  const tabs = getTabs({
    projectId: projectId || "",
    objectId: objectId || "",
    mainJournalId: mainJournalId || "",
    titleId: titleId || "",
    mainJournalVersionId: tabsJournalVersionId,
    isJournalVersionMode: isTabsVersionMode,
    status: journalTitle?.mainJournal?.titleJournal?.status || "NEW",
    routes,
  });

  const [activeKey, setActiveKey] = useState<string>();

  useEffect(() => {
    const titleTabKey = tabs[0]?.key;
    setActiveKey(
      isTitleVersionDetail && titleTabKey ? titleTabKey : location.pathname,
    );
  }, [isTitleVersionDetail, location.pathname, tabs]);

  const onTabChange = (key: string) => {
    navigate(key);
  };

  if (isTitleAmendmentsForm) {
    return null;
  }

  return (
    <Tabs
      activeKey={activeKey}
      items={tabs}
      className="project-custom-tabs project-custom-tabs-blue"
      onChange={onTabChange}
      tabBarStyle={{
        overflow: "hidden",
      }}
      tabBarGutter={32}
      style={{ marginBottom: "32px" }}
    />
  );
};
